import { connect } from 'react-redux';
import { get, isEmpty, keyBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

// Services
import transfersService from '@services/transfers';

// Actions
import { showErrorMessage } from '@actions/messageconfirmation';

// Utils
import { convertSPPQuantityInMasterUnit } from '@orders/utils/computePackagingToSupplierProduct';
import { convertUnits, getFactorToConvertMetricToEntityUnit } from '@commons/utils/conversion';
import { TRANSFER_DETAILS_VIEW } from '@stocks/StocksInventories/Transfers/utils/constants';
import { translatedUnitCondensed } from '@commons/utils/translateUnit';

// Commons
import {
  Button,
  Dropdown,
  INPUT_WIDTH,
  InputIncrement,
} from '@commons/utils/styledLibraryComponents';
import { EmptyStateIcon } from '@commons/EmptyState/styledComponents';
import CustomDrawer from '@commons/Drawer';
import DisplayNumber from '@commons/DisplayNumber';
import Footer from '@commons/Footer/Footer';

import {
  BUTTON_STYLE,
  ButtonsContainer,
  Container,
  Content,
  ContentContainer,
  DetailContainer,
  Divider,
  DrawerText,
  DropdownItem,
  DropdownItemImage,
  DropdownItemMainValue,
  DropdownItemSecondaryValue,
  DropdownItemValuesContainer,
  EmptyStateContainer,
  InputIncrementContainer,
  RequiredTextContainer,
  TitleContainer,
  TotalContainer,
  TotalsContainer,
} from './styledComponents';

const ReferenceDrawer = (props) => {
  const {
    isOpen,
    onClose,
    selectedTab,
    transferList,
    transferDetailsView,
    recipesToDisplay: alreadyAddedRecipes,
    supplierProductsToDisplay: alreadyAddedSupplierProducts,
    setRecipesToDisplay,
    setSupplierProductsToDisplay,
    showErrorMessage,
    transferCostDate,
  } = props;

  const isSupplierProductTabSelected = (selectedTab) => selectedTab === 0;

  const [availableReferences, setAvailableReferences] = useState([]);
  const [selectedReference, setSelectedReference] = useState(null);
  const [totalHT, setTotalHT] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !transferList.senderStoreId) {
      return;
    }

    setIsLoading(true);

    (async () => {
      const { supplierProducts, recipes } = await getAvailableSupplierProductsAndRecipes(
        transferList.senderStoreId,
      );

      setAvailableReferences(
        isSupplierProductTabSelected(selectedTab) ? supplierProducts : recipes,
      );

      setIsLoading(false);
    })();
  }, [isOpen, selectedTab, transferList.senderStoreId]);

  const getAvailableSupplierProductsAndRecipes = async (storeId) => {
    const alreadyAddedSupplierProductsKeyById = keyBy(alreadyAddedSupplierProducts, 'id');
    const alreadyAddedRecipesKeyById = keyBy(alreadyAddedRecipes, 'id');

    try {
      // sentAt (existing transfer) wins server-side; creation has no sentAt yet and sends the picked day
      const { supplierProducts, recipes } =
        await transfersService.getSupplierProductsAndRecipesByStoreId(storeId, {
          date: transferCostDate,
          sentAt: transferList.sentAt,
        });

      const formattedSupplierProducts = supplierProducts.reduce((acc, supplierProduct) => {
        if (alreadyAddedSupplierProductsKeyById[supplierProduct.id]) {
          return acc;
        }

        const supplierName = get(supplierProduct, 'lnkSupplierSupplierproductrel.name', null);

        acc.push({
          ...supplierProduct,
          selectedPackagingId: supplierProduct.packagings[0].id,
          packagings: supplierProduct.packagings.map((packaging) => ({
            ...packaging,
            selectedQuantity: 0,
            quantityInMasterUnit: convertSPPQuantityInMasterUnit(
              packaging.id,
              supplierProduct.packagings,
            ),
          })),
          renderValue: () => (
            <DropdownItem>
              <DropdownItemImage
                src={supplierProduct.img || '/images/inpulse/product-photo-placeholder.svg'}
              />
              <DropdownItemValuesContainer>
                <DropdownItemMainValue>{supplierProduct.name}</DropdownItemMainValue>
                <DropdownItemSecondaryValue>{supplierName}</DropdownItemSecondaryValue>
              </DropdownItemValuesContainer>
            </DropdownItem>
          ),
        });

        return acc;
      }, []);

      const formattedRecipes = recipes.reduce((acc, recipe) => {
        if (alreadyAddedRecipesKeyById[recipe.id]) {
          return acc;
        }

        acc.push({
          ...recipe,
          selectedQuantity: 0,
          renderValue: () => (
            <DropdownItem>
              <DropdownItemImage
                src={recipe.img || '/images/inpulse/product-photo-placeholder.svg'}
              />
              <DropdownItemValuesContainer>
                <DropdownItemMainValue>{recipe.name}</DropdownItemMainValue>
                <DropdownItemSecondaryValue>{recipe.category}</DropdownItemSecondaryValue>
              </DropdownItemValuesContainer>
            </DropdownItem>
          ),
        });

        return acc;
      }, []);

      return { supplierProducts: formattedSupplierProducts, recipes: formattedRecipes };
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.DETAILS_FETCH_FAILURE'));
      return { supplierProducts: [], recipes: [] };
    }
  };

  const handleClose = () => {
    setSelectedReference(null);
    setTotalHT(0);
    setTotalUnits(0);
    onClose();
  };

  const handleQuantityChange = (packagingId, newQuantity) => {
    if (isSupplierProductTabSelected(selectedTab)) {
      const updatedReference = {
        ...selectedReference,
        packagings: selectedReference.packagings.map((packaging) =>
          packaging.id === packagingId
            ? {
                ...packaging,
                selectedQuantity: newQuantity,
              }
            : packaging,
        ),
      };
      setSelectedReference(updatedReference);
      calculateTotals(updatedReference);
    } else {
      const updatedReference = {
        ...selectedReference,
        selectedQuantity: newQuantity,
      };
      setSelectedReference(updatedReference);
      calculateTotals(updatedReference);
    }
  };

  const calculateTotals = (reference) => {
    if (isSupplierProductTabSelected(selectedTab)) {
      let totalHT = 0;
      let totalUnits = 0;

      reference.packagings.forEach(({ selectedQuantity, price, quantityInMasterUnit }) => {
        totalHT += selectedQuantity * (price || 0);
        totalUnits += selectedQuantity * quantityInMasterUnit;
      });

      setTotalHT(totalHT);
      setTotalUnits(totalUnits);
      return;
    }

    const referenceTotalCost =
      reference.selectedQuantity *
      (reference.cost || 0) *
      getFactorToConvertMetricToEntityUnit(reference.unit);

    setTotalHT(referenceTotalCost);
    setTotalUnits(reference.selectedQuantity);
    return;
  };

  const renderAddedReferenceQuantities = () => {
    if (!selectedReference) {
      return;
    }

    if (isSupplierProductTabSelected(selectedTab)) {
      const packagings = selectedReference.packagings;

      if (isEmpty(packagings)) {
        return;
      }

      return packagings.map(({ id, name, selectedQuantity }) => (
        <DetailContainer key={id}>
          <InputIncrementContainer>
            <InputIncrement
              label={`${name} :`}
              limitValues={{ min: 0 }}
              setValue={(value) => handleQuantityChange(id, value)}
              value={selectedQuantity}
            />
          </InputIncrementContainer>
        </DetailContainer>
      ));
    }

    const unit = translatedUnitCondensed(convertUnits(selectedReference.unit));

    return (
      <DetailContainer>
        <InputIncrementContainer>
          <InputIncrement
            label={`${unit} :`}
            limitValues={{ min: 0 }}
            setValue={(value) => handleQuantityChange(null, value)}
            value={selectedReference.selectedQuantity}
          />
        </InputIncrementContainer>
      </DetailContainer>
    );
  };

  const renderTotals = () => {
    if (!selectedReference) return null;

    const unit = isSupplierProductTabSelected(selectedTab)
      ? i18next.t('GENERAL.UNIT')
      : translatedUnitCondensed(convertUnits(selectedReference.unit));

    return (
      <TotalsContainer>
        <TotalContainer>
          <DrawerText isBold>{i18next.t('GENERAL.TOTAL_EX_TAX')}</DrawerText>
          <DrawerText isBold>
            <DisplayNumber number={totalHT} displayCurrencyCode />
          </DrawerText>
        </TotalContainer>
        <TotalContainer>
          <DrawerText isBold>
            {i18next.t('GENERAL.TOTAL')} {unit}
          </DrawerText>
          <DrawerText isBold>
            {totalUnits.toFixed(2)} {unit}
          </DrawerText>
        </TotalContainer>
      </TotalsContainer>
    );
  };

  const handleValidate = () => {
    const formattedReference = {
      ...selectedReference,
      isNewlyAdded: true,
      ...(transferDetailsView === TRANSFER_DETAILS_VIEW.DELIVERY && {
        transferNoteQuantity: null,
      }),
      ...(transferDetailsView === TRANSFER_DETAILS_VIEW.RECEPTION && {
        transferNoteQuantity: null,
        deliveryNoteQuantity: null,
      }),
    };

    if (isSupplierProductTabSelected(selectedTab)) {
      setSupplierProductsToDisplay([...alreadyAddedSupplierProducts, formattedReference]);
    } else {
      setRecipesToDisplay([...alreadyAddedRecipes, formattedReference]);
    }

    handleClose();
  };

  return (
    <CustomDrawer direction="right" isOpened={isOpen} onClick={handleClose}>
      <Container>
        <ContentContainer>
          <TitleContainer>{i18next.t('ORDERS.ORDERS.DRAWER_TITLE_ADD_REFERENCE')}</TitleContainer>

          <Content>
            <RequiredTextContainer>
              <DrawerText>{i18next.t('GENERAL.REFERENCE')} :</DrawerText>
            </RequiredTextContainer>
            <Dropdown
              iconSrc={'/images/inpulse/search-dmgrey-small.svg'}
              isDisabled={isLoading}
              items={availableReferences}
              placeholder={i18next.t(isLoading ? 'GENERAL.LOADING' : 'GENERAL.SEARCH')}
              selectedItem={selectedReference}
              width={INPUT_WIDTH.EXTRA_LARGE}
              isRequired
              onSelectionChange={(reference) => {
                setSelectedReference(reference);
                setTotalHT(0);
                setTotalUnits(0);
              }}
            />
          </Content>

          <Divider />

          {isEmpty(selectedReference) ? (
            <Content>
              <EmptyStateContainer>
                <EmptyStateIcon
                  alt="empty-state-no-result-icon"
                  className={'center'}
                  src="/images/inpulse/empty-state-no-result.svg"
                />
                <DrawerText>
                  {i18next.t('ORDERS.ORDERS.DRAWER_EMPTY_STATE_REFERENCE_NOT_SELECTED')}
                </DrawerText>
              </EmptyStateContainer>
            </Content>
          ) : (
            <Content>
              <DrawerText>{`${i18next.t('STOCKS.TRANSFERS.PACKAGINGS_QUANTITES')} :`}</DrawerText>
              {renderAddedReferenceQuantities()}
            </Content>
          )}

          {!isEmpty(selectedReference) && (
            <>
              <Divider />
              {renderTotals()}
            </>
          )}
        </ContentContainer>

        <Divider />

        <Footer
          customStyle={{
            'box-shadow': 'none',
            'background-color': 'transparent',
            width: 'auto',
            height: 'calc(100% - calc(100% - (64px + 24px)))',
            padding: '24px 0px',
          }}
        >
          <ButtonsContainer>
            <Button
              buttonCustomStyle={BUTTON_STYLE}
              color={'inpulse-outline'}
              handleClick={handleClose}
              icon={'/images/inpulse/close-black-small.svg'}
              label={i18next.t('GENERAL.CANCEL')}
              minWidth={168}
            />
            <Button
              buttonCustomStyle={BUTTON_STYLE}
              color={'inpulse-default'}
              handleClick={handleValidate}
              icon={'/images/inpulse/check-white-small.svg'}
              isDisabled={!selectedReference}
              label={i18next.t('GENERAL.VALIDATE')}
              minWidth={168}
            />
          </ButtonsContainer>
        </Footer>
      </Container>
    </CustomDrawer>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => dispatch(showErrorMessage(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReferenceDrawer);
